import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  YAxis,
  XAxis,
} from "recharts";
import { Crosshair, Plus } from "lucide-react";
import { fmtPrice, fmtPct } from "../../lib/format";

const TIMEFRAMES = ["M1", "M5", "M15", "H1", "H4"];

export default function PriceChart({ snapshot, onNewSignal }) {
  const [tf, setTf] = useState("M15");
  const market = snapshot?.market || {};
  const positions = snapshot?.positions || [];

  const data = useMemo(
    () => (market.points || []).map((p, i) => ({ i, p: Number(p.p) })),
    [market.points]
  );

  const prices = data.map((d) => d.p).filter((v) => v > 0);
  const activeSell = positions.find((p) => p.side === "SELL");
  const activeBuy = positions.find((p) => p.side === "BUY");
  const ref = activeSell || activeBuy;

  const overlays = [];
  if (ref) {
    overlays.push({ y: ref.sl, color: "#EF4444", label: "SL" });
    overlays.push({ y: ref.tp, color: "#10B981", label: "TP" });
    overlays.push({ y: ref.priceOpen, color: "#F59E0B", label: "OPEN" });
  }

  const allY = [...prices, ...overlays.map((o) => o.y)].filter((v) => v > 0);
  const min = allY.length ? Math.min(...allY) : 0;
  const max = allY.length ? Math.max(...allY) : 100;
  const pad = (max - min) * 0.08 || 1;

  const last = data.length ? data[data.length - 1].p : Number(market.bid || 0);
  const first = data.length ? data[0].p : last;
  const change = first ? ((last - first) / first) * 100 : 0;
  const up = change >= 0;

  return (
    <div
      id="overview"
      data-testid="xauusd-live-chart-panel"
      className="panel p-5 relative float-in"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <span className="w-6 h-6 rounded-md grid place-items-center bg-amber-500/15 text-amber-400">
              <Crosshair size={13} />
            </span>
            Grafico XAUUSD · Oro
            <span className="ml-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/12 text-emerald-400 border border-emerald-500/25">
              ● LIVE FEED
            </span>
          </div>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="font-mono text-3xl font-extrabold tracking-tight text-sky-300">
              {fmtPrice(last)}
              <span className="text-slate-500 text-xs font-medium ml-1">USD / oz</span>
            </span>
            <span className={`font-mono text-sm font-semibold ${up ? "text-emerald-400" : "text-rose-400"}`}>
              {fmtPct(change)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-[#0F1728] border border-white/10 rounded-lg p-1">
            {TIMEFRAMES.map((t) => (
              <button
                key={t}
                data-testid={`tf-btn-${t.toLowerCase()}`}
                onClick={() => setTf(t)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-semibold transition-colors ${
                  tf === t ? "bg-amber-500/20 text-amber-400" : "text-slate-500 hover:text-slate-200"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            data-testid="open-add-signal-dialog-button"
            onClick={onNewSignal}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 transition-colors"
          >
            <Plus size={13} /> Segnale Oro
          </button>
        </div>
      </div>

      <div className="h-[300px] w-full min-w-0" style={{ minHeight: 300 }}>
        <ResponsiveContainer width="100%" height={300} minWidth={200}>
          <AreaChart data={data} margin={{ top: 8, right: 56, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="goldArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="i" hide />
            <YAxis
              domain={[min - pad, max + pad]}
              orientation="right"
              width={54}
              tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => v.toFixed(1)}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                background: "#121824",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
                fontFamily: "JetBrains Mono",
                fontSize: 12,
              }}
              labelStyle={{ display: "none" }}
              formatter={(v) => [`${fmtPrice(v)} USD`, "XAUUSD"]}
            />
            {overlays.map((o, idx) => (
              <ReferenceLine
                key={idx}
                y={o.y}
                stroke={o.color}
                strokeDasharray="4 4"
                strokeOpacity={0.8}
                label={{
                  value: `${o.label} ${fmtPrice(o.y)}`,
                  position: "right",
                  fill: o.color,
                  fontSize: 9,
                  fontFamily: "JetBrains Mono",
                }}
              />
            ))}
            <Area
              type="monotone"
              dataKey="p"
              stroke="#F59E0B"
              strokeWidth={2}
              fill="url(#goldArea)"
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap gap-4 border-t border-white/10 pt-3 mt-2 text-[10px] text-slate-400">
        <Legend color="#F59E0B" label="Prezzo / Zona OPEN" />
        <Legend color="#EF4444" label="Stop Loss" />
        <Legend color="#10B981" label="Take Profit" />
        <span className="ml-auto font-mono text-slate-500">
          Bid {fmtPrice(market.bid)} · Ask {fmtPrice(market.ask)}
        </span>
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
